import * as React from 'react';
import { Pressable, View } from 'react-native';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader } from './card';
import { Text } from './text';

interface ChecklistItem {
  id: string;
  label: string;
  completed: boolean;
}

interface ProgressiveChecklistProps {
  items: ChecklistItem[];
  onDismiss?: () => void;
  onItemPress?: (item: ChecklistItem) => void;
  title?: string;
  className?: string;
  testID?: string;
}

function ProgressiveChecklist({
  items,
  onDismiss,
  onItemPress,
  title = 'Get started',
  className,
  testID,
}: ProgressiveChecklistProps) {
  return (
    <Card className={className} testID={testID}>
      <CardHeader>
        <View className="flex-row items-center justify-between">
          <Text className="text-lg font-semibold text-card-foreground">
            {title}
          </Text>
          {onDismiss && (
            <Pressable
              onPress={onDismiss}
              accessibilityLabel="Dismiss checklist"
              accessibilityRole="button"
              className="p-1"
            >
              <Text className="text-muted-foreground text-lg">✕</Text>
            </Pressable>
          )}
        </View>
      </CardHeader>
      <CardContent>
        {items.map((item) => (
          <Pressable
            key={item.id}
            testID={`checklist-item-${item.id}`}
            onPress={() => onItemPress?.(item)}
            accessibilityRole="button"
            className="flex-row items-center gap-3 py-2"
          >
            <View
              testID={`check-indicator-${item.id}`}
              accessibilityLabel={item.completed ? 'Completed' : 'Not completed'}
              className={cn(
                'w-5 h-5 rounded-full items-center justify-center border',
                item.completed
                  ? 'bg-primary border-primary'
                  : 'border-muted-foreground',
              )}
            >
              {item.completed && (
                <Text className="text-primary-foreground text-xs">✓</Text>
              )}
            </View>
            <Text
              className={cn(
                'text-base',
                item.completed
                  ? 'text-muted-foreground line-through'
                  : 'text-card-foreground',
              )}
            >
              {item.label}
            </Text>
          </Pressable>
        ))}
      </CardContent>
    </Card>
  );
}

export { ProgressiveChecklist };
export type { ProgressiveChecklistProps, ChecklistItem };
